/* SHOPIFY_PERF_VERSION_RORRYJP_20260918_R4 — preserve presentation, schedule resources by visibility. */
(() => {
  'use strict';
  const config = document.currentScript.dataset;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const assets = new Map();
  let swiperPromise;

  function loadAsset(url, css) {
    if (assets.has(url)) return assets.get(url);
    const promise = new Promise((resolve, reject) => {
      const node = document.createElement(css ? 'link' : 'script');
      if (css) { node.rel = 'stylesheet'; node.href = url; }
      else { node.src = url; node.async = true; }
      node.onload = () => { clearTimeout(timer); resolve(); };
      const fail = () => { clearTimeout(timer); node.remove(); assets.delete(url); reject(new Error('Resource unavailable')); };
      node.onerror = fail;
      const timer = setTimeout(fail, 20000);
      document.head.append(node);
    });
    assets.set(url, promise);
    return promise;
  }

  function loadSwiper() {
    if (!swiperPromise) {
      swiperPromise = Promise.all([
        loadAsset(config.swiperStyle, true),
        window.Swiper ? Promise.resolve() : loadAsset(config.swiperScript, false)
      ]).catch(error => { swiperPromise = null; throw error; });
    }
    return swiperPromise;
  }

  function restoreImage(template) {
    if (!template?.isConnected) return;
    const fragment = template.content.cloneNode(true);
    const image = fragment.querySelector('img');
    if (image) image.loading = 'eager';
    template.replaceWith(fragment);
    return image;
  }

  class RorryImage extends HTMLElement {
    connectedCallback() {
      if (this.observer || !this.querySelector('template[data-rorry-image]')) return;
      if (!('IntersectionObserver' in window)) return this.reveal();
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.reveal();
      }, { rootMargin: '300px 100px' });
      // display:contents has no box; observe the stable card image container.
      this.observer.observe(this.parentElement);
    }
    reveal() {
      if (!this.isConnected) return;
      const parent = this.parentElement;
      restoreImage(this.querySelector('template[data-rorry-image]'));
      this.observer?.disconnect();
      this.observer = null;
      // Restore the original direct-child structure used by media layout selectors.
      this.replaceWith(...this.childNodes);
      parent.dispatchEvent(new CustomEvent('rorry:image-ready', { bubbles: true }));
    }
    disconnectedCallback() { this.observer?.disconnect(); this.observer = null; }
  }

  // Delegation also covers collection filtering, predictive results and quick views.
  function revealHover(event) {
    const card = event.target.closest?.('.category-card, .product-card');
    if (!card) return;
    card.querySelectorAll('template[data-rorry-hover]').forEach(template => {
      const image = restoreImage(template);
      if (!image) return;
      const ready = () => { if (image.naturalWidth) card.dataset.hoverReady = 'true'; };
      image.addEventListener('load', ready, { once: true });
      ready();
    });
  }
  document.addEventListener('pointerover', revealHover, { passive: true });
  document.addEventListener('focusin', revealHover);
  document.addEventListener('pointerdown', revealHover, { passive: true });

  class RorryCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.visible = false;
      this.addEventListener('focusin', () => this.start(), { signal });
      this.addEventListener('pointerdown', () => this.start(), { signal, passive: true });
      this.addEventListener('click', async event => {
        const button = event.target.closest('.fr-prev, .fr-next');
        if (!button || this.instance) return;
        await this.start();
        if (this.instance) this.instance[button.matches('.fr-next') ? 'slideNext' : 'slidePrev']();
      }, { signal });
      this.addEventListener('shopify:block:select', () => this.start(), { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      reducedMotion.addEventListener('change', () => this.updatePlayback(), { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          if (this.visible) this.start();
          this.updatePlayback();
        }, { rootMargin: '300px 0px' });
        this.observer.observe(this);
      } else { this.visible = true; this.start(); }
    }

    start() {
      if (this.instance) return Promise.resolve();
      if (this.starting) return this.starting;
      const slider = this.querySelector('.js-zl-ins-slider, .fr-products-slider');
      if (!slider || slider.dataset.mode === 'grid' || !slider.querySelector('.swiper-slide')) return Promise.resolve();
      const controller = this.controller;
      this.starting = loadSwiper().then(() => {
        if (!this.isConnected || controller.signal.aborted) return;
        const gallery = this.dataset.kind === 'gallery';
        const gap = Number(this.dataset.gap) || 16;
        const options = gallery ? {
          slidesPerView: Number(this.dataset.mobileCols) || 2, spaceBetween: 10, loop: true,
          autoplay: slider.dataset.autoplay === 'true' ? { delay: Number(slider.dataset.speed) || 3000, disableOnInteraction: false } : false,
          breakpoints: { 768: { slidesPerView: 3, spaceBetween: gap }, 1024: { slidesPerView: Number(this.dataset.desktopCols) || 5, spaceBetween: gap } }
        } : {
          loop: false, rewind: true, speed: 600, slidesPerView: 1.2, spaceBetween: 12, slidesPerGroup: 1, grabCursor: true,
          breakpoints: { 480: { slidesPerView: 2, spaceBetween: 14 }, 768: { slidesPerView: 3, spaceBetween: 16 }, 1024: { slidesPerView: 4, spaceBetween: 18 }, 1280: { slidesPerView: 5, spaceBetween: 20 } },
          navigation: { prevEl: this.querySelector('.fr-prev'), nextEl: this.querySelector('.fr-next') }
        };
        options.pagination = { el: this.querySelector('.swiper-pagination'), clickable: true };
        // Stable card sizes are already present; no ancestor MutationObserver is needed.
        this.dataset.ready = 'true';
        this.instance = new window.Swiper(slider, options);
        this.updatePlayback();
        delete this.dataset.loadFailed;
      }).catch(() => {
        delete this.dataset.ready;
        this.dataset.loadFailed = 'true'; // Native horizontal scrolling remains available.
      }).finally(() => { if (this.controller === controller) this.starting = null; });
      return this.starting;
    }

    updatePlayback() {
      if (!this.instance?.autoplay) return;
      if (this.visible && !document.hidden && !reducedMotion.matches) this.instance.autoplay.resume();
      else this.instance.autoplay.pause();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.instance?.destroy(true, true);
      this.controller = null; this.instance = null; this.starting = null;
      delete this.dataset.ready;
    }
  }

  class RorryVideoMarquee extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.visibleVideos = new Set();
      this.videos = [...this.querySelectorAll('video')];
      this.videos.forEach(video => {
        video.addEventListener('playing', () => {
          video.closest('.fr-video-inner').classList.add('is-playing');
          video.controls = reducedMotion.matches;
          if (!this.visibleVideos.has(video) || document.hidden) video.pause();
        }, { signal });
        video.addEventListener('error', () => { video.controls = true; }, { signal });
      });
      const sync = () => {
        this.toggleAttribute('data-offscreen', document.hidden || !this.sectionVisible);
        this.videos.forEach(video => this.updateVideo(video));
      };
      document.addEventListener('visibilitychange', sync, { signal });
      reducedMotion.addEventListener('change', sync, { signal });
      if ('IntersectionObserver' in window) {
        this.sectionObserver = new IntersectionObserver(entries => { this.sectionVisible = entries[0].isIntersecting; sync(); });
        this.sectionObserver.observe(this);
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.01) this.visibleVideos.add(entry.target);
            else this.visibleVideos.delete(entry.target);
            this.updateVideo(entry.target);
          });
        }, { threshold: [0, 0.01] });
        this.videos.forEach(video => this.observer.observe(video));
      } else {
        this.videos.forEach(video => {
          video.controls = true;
          const activate = () => { this.visibleVideos.add(video); this.updateVideo(video); };
          video.addEventListener('pointerdown', activate, { signal, passive: true });
          video.addEventListener('focus', activate, { signal });
        });
      }
    }
    updateVideo(video) {
      if (!this.visibleVideos.has(video) || document.hidden) { video.pause(); video.preload = 'none'; return; }
      if (!video.hasAttribute('src')) {
        video.src = matchMedia('(max-width: 768px)').matches ? video.dataset.srcMobile : video.dataset.srcDesktop;
        video.load();
      }
      if (reducedMotion.matches) { video.pause(); video.controls = true; return; }
      video.muted = true;
      video.play().catch(() => { video.controls = true; });
    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.sectionObserver?.disconnect(); this.controller?.abort();
      this.videos?.forEach(video => video.pause()); this.controller = null;
    }
  }
  for (const [name, ctor] of [['rorry-image', RorryImage], ['rorry-carousel', RorryCarousel], ['rorry-video-marquee', RorryVideoMarquee]]) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }
  window.RorryPerformance = { loadSwiper };
  document.dispatchEvent(new CustomEvent('rorry:ready'));
})();
